import type { LucideIcon } from 'lucide-react'
import type { DashboardStats } from '@/shared/api/types/dashboard-types'
import { Globe, Languages, MapPin, Route, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AdminScopeType } from '@/shared/constants/admin-scope'
import { cn } from '@/shared/libs/cn'
import { Skeleton } from '@/shared/ui/skeleton'

const STAT_KEYS = {
  tenants: 'tenants',
  pois: 'pois',
  tours: 'tours',
  destinations: 'destinations',
  languages: 'languages',
} as const

interface DashboardStatCardsProps {
  stats: DashboardStats
  variant: AdminScopeType
}

interface StatCardData {
  key: string
  title: string
  value: number
  subtitle: string
  icon: LucideIcon
  iconBg: string
  iconColor: string
}

function StatCard({ data }: { data: StatCardData }) {
  const Icon = data.icon

  return (
    <div className={cn('bg-white rounded-xl border border-zinc-200 p-5')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="min-h-10 break-words text-sm leading-5 text-zinc-500">{data.title}</p>
          <p className="mt-1 text-3xl font-bold text-zinc-900 tabular-nums">{data.value}</p>
          <p className="mt-1 text-xs text-zinc-400 truncate">{data.subtitle}</p>
        </div>
        <div className={cn('flex-shrink-0 rounded-lg p-2', data.iconBg)}>
          <Icon className={cn('h-5 w-5', data.iconColor)} />
        </div>
      </div>
    </div>
  )
}

export function DashboardStatCards({ stats, variant }: DashboardStatCardsProps) {
  const { t } = useTranslation()

  const tenantCard: StatCardData = {
    key: STAT_KEYS.tenants,
    title: t('dashboard.totalTenants'),
    value: stats.totalTenants,
    subtitle: t('dashboard.activeOfTotal', {
      active: stats.activeTenants,
      total: stats.totalTenants,
    }),
    icon: Users,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  }

  const cards: StatCardData[] = [
    ...(variant === AdminScopeType.Super ? [tenantCard] : []),
    ...(variant === AdminScopeType.Super
      ? [
          {
            key: STAT_KEYS.destinations,
            title: t('dashboard.totalDestinations'),
            value: stats.totalDestinations,
            subtitle: t('dashboard.activeOfTotal', {
              active: stats.activeDestinations,
              total: stats.totalDestinations,
            }),
            icon: Globe,
            iconBg: 'bg-purple-50',
            iconColor: 'text-purple-600',
          },
        ]
      : []),
    {
      key: STAT_KEYS.pois,
      title: t('dashboard.totalPois'),
      value: stats.totalPois,
      subtitle: t('dashboard.activeOfTotal', {
        active: stats.activePois,
        total: stats.totalPois,
      }),
      icon: MapPin,
      iconBg: 'bg-cyan-50',
      iconColor: 'text-cyan-600',
    },
    {
      key: STAT_KEYS.tours,
      title: t('dashboard.totalTours'),
      value: stats.totalTours,
      subtitle: t('dashboard.activeOfTotal', {
        active: stats.activeTours,
        total: stats.totalTours,
      }),
      icon: Route,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      key: STAT_KEYS.languages,
      title: t('dashboard.languagesInUse'),
      value: stats.languagesInUse,
      subtitle: t('dashboard.uniqueLanguages'),
      icon: Languages,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
    },
  ]

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
      {cards.map((card) => (
        <StatCard key={card.key} data={card} />
      ))}
    </div>
  )
}

export function DashboardStatCardsSkeleton({ variant }: { variant: AdminScopeType }) {
  const cardCount = variant === AdminScopeType.Super ? 5 : 3
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
      {Array.from({ length: cardCount }, (_, i) => (
        <div key={i} className="bg-white rounded-xl border border-zinc-200 p-5">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-16 mb-1" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>
  )
}
